
// Returns a function, that, as long as it continues to be invoked, will not be triggered.
export function debounce(func, wait) {
  var timeout, args, context, timestamp, result;

  var later = function() {
    var last = Date.now() - timestamp;

    if (last < wait && last >= 0) {
      timeout = setTimeout(later, wait - last);
    } else {
      timeout = null;
      result = func.apply(context, args);
      context = args = null;
    }
  };

  function debounced() {
    context = this;
    args = arguments;
    timestamp = Date.now();
    if (!timeout) timeout = setTimeout(later, wait);

    return result;
  }

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
    }
  }

  return debounced;
}

// Returns a function, that, when invoked, will only be triggered at most once during a given window of time.
export function throttle(func, wait) {
  var context, args, result;
  var timeout = null;
  var previous = 0;

  function later() {
    previous = Date.now();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  }

  return function() {
    var now = Date.now();
    if (!previous) previous = now;
    var remaining = wait - (now - previous);
    context = this;
    args = arguments;

    if (remaining <= 0 || remaining > wait) {

      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }

      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout) {
      timeout = setTimeout(later, remaining);
    }

    return result;
  };
}

// A generic touch event handler which recreates mouse events
export function touchHandler(event) {
  const first = event.changedTouches[0];
  let type;

  switch (event.type) {
    case 'touchstart':
      type = 'mousedown';
      break;
    case 'touchmove':
      type = 'mousemove';
      break;
    case 'touchend':
      type = 'mouseup';
      break;
    default:
      return;
  }

  const simulatedEvent = document.createEvent('MouseEvent');
  simulatedEvent.initMouseEvent(
    type,
    true,
    true,
    window,
    1,
    first.screenX,
    first.screenY,
    first.clientX,
    first.clientY,
    false,
    false,
    false,
    false,
    0,
    null
  );

  first.target.dispatchEvent(simulatedEvent);
  event.preventDefault();
}

// Get the position of the canvas relative to the document
export function getCanvasBox(canvas) {
  const bodyRect = document.body.getBoundingClientRect();
  const canvasRect = canvas.getBoundingClientRect();

  return {
    left: canvasRect.left - bodyRect.left,
    top: canvasRect.top - bodyRect.top,
    width: canvasRect.width,
    height: canvasRect.height
  };
}

// Get the location of the mouse on the canvas
export function getMouseLocation({
  canvasX,
  canvasY,
  cropWindow,
  ratio,
  handleSize
}) {

  if (
    canvasX > cropWindow.x - (handleSize / 2) &&
    canvasX < cropWindow.x + (handleSize / 2) &&
    canvasY > cropWindow.y - (handleSize / 2) &&
    canvasY < cropWindow.y + (handleSize / 2)
  ) {
    return 'nw-resize';
  }

  if (
    canvasX > cropWindow.x + cropWindow.width - (handleSize / 2) &&
    canvasX < cropWindow.x + cropWindow.width + (handleSize / 2) &&
    canvasY > cropWindow.y - (handleSize / 2) &&
    canvasY < cropWindow.y + (handleSize / 2)
  ) {
    return 'ne-resize';
  }

  if (
    canvasX > cropWindow.x + cropWindow.width - (handleSize / 2) &&
    canvasX < cropWindow.x + cropWindow.width + (handleSize / 2) &&
    canvasY > cropWindow.y + cropWindow.height - (handleSize / 2) &&
    canvasY < cropWindow.y + cropWindow.height + (handleSize / 2)
  ) {
    return 'se-resize';
  }

  if (
    canvasX > cropWindow.x - (handleSize / 2) &&
    canvasX < cropWindow.x + (handleSize / 2) &&
    canvasY > cropWindow.y + cropWindow.height - (handleSize / 2) &&
    canvasY < cropWindow.y + cropWindow.height + (handleSize / 2)
  ) {
    return 'sw-resize';
  }

  if (
    !ratio &&
    canvasX > cropWindow.x + (cropWindow.width / 2) - (handleSize / 2) &&
    canvasX < cropWindow.x + (cropWindow.width / 2) + (handleSize / 2) &&
    canvasY > cropWindow.y - (handleSize / 2) &&
    canvasY < cropWindow.y + (handleSize / 2)
  ) {
    return 'n-resize';
  }

  if (
    !ratio &&
    canvasX > cropWindow.x + cropWindow.width - (handleSize / 2) &&
    canvasX < cropWindow.x + cropWindow.width + (handleSize / 2) &&
    canvasY > cropWindow.y + (cropWindow.height / 2) - (handleSize / 2) &&
    canvasY < cropWindow.y + (cropWindow.height / 2) + (handleSize / 2)
  ) {
    return 'e-resize';
  }

  if (
    !ratio &&
    canvasX > cropWindow.x + (cropWindow.width / 2) - (handleSize / 2) &&
    canvasX < cropWindow.x + (cropWindow.width / 2) + (handleSize / 2) &&
    canvasY > cropWindow.y + cropWindow.height - (handleSize / 2) &&
    canvasY < cropWindow.y + cropWindow.height + (handleSize / 2)
  ) {
    return 's-resize';
  }

  if (
    !ratio &&
    canvasX > cropWindow.x - (handleSize / 2) &&
    canvasX < cropWindow.x + (handleSize / 2) &&
    canvasY > cropWindow.y + (cropWindow.height / 2) - (handleSize / 2) &&
    canvasY < cropWindow.y + (cropWindow.height / 2) + (handleSize / 2)
  ) {
    return 'w-resize';
  }

  if (
    canvasX > cropWindow.x &&
    canvasX < cropWindow.x + cropWindow.width &&
    canvasY > cropWindow.y &&
    canvasY < cropWindow.y + cropWindow.height
  ) {
    return 'selection';
  }

  return '';
}

// Get the proper cursor for the given point on a transformed canvas
export function getCursor(position, { flipped, flopped, rotation }) {

  // We're dragging here
  if (position === 'selection') {
    return 'move';
  }

  // Not currently over any target, we assume we're drawing
  if (position === '') {
    return 'crosshair';
  }

  // Every other cursor is a resizer
  const cursors = {
    'nw-resize': 'nwse-resize',
    'n-resize': 'ns-resize',
    'ne-resize': 'nesw-resize',
    'e-resize': 'ew-resize',
    'se-resize': 'nwse-resize',
    's-resize': 'ns-resize',
    'sw-resize': 'nesw-resize',
    'w-resize': 'ew-resize'
  };
  const positions = Object.keys(cursors);

  // The sides need to move 2 positions over if the canvas is rotated
  if (
    positions.indexOf(position) % 2 !== 0 &&
    (rotation === 90 || rotation === 270)
  ) {
    return cursors[positions[(positions.indexOf(position) + 2) % positions.length]];
  }

  // The corners need to move 2 positions over if the canvas is rotated without being flipped or flopped
  if (
    positions.indexOf(position) % 2 === 0 &&
    (rotation === 90 || rotation === 270) &&
    ((flipped && flopped) || !(flipped || flopped))
  ) {
    return cursors[positions[(positions.indexOf(position) + 2) % positions.length]];
  }

  // The corners need to move 2 positions over if the canvas is flipped or flopped without rotation
  if (
    positions.indexOf(position) % 2 === 0 &&
    (rotation !== 90 && rotation !== 270) &&
    ((flipped || flopped) && !(flipped && flopped))
  ) {
    return cursors[positions[(positions.indexOf(position) + 2) % positions.length]];
  }

  // Nothing needed to change
  return cursors[position];
}

// When rotating, we have to scale down the image
function getScaleAmount(layer, { rotation }) {
  let scaleAmount = 1;

  // If the canvas is not 1:1, then we have to scale down when rotating
  if (rotation === 90 || rotation === 270) {

    if (layer.imageWidth > layer.canvas.height) {
      scaleAmount = layer.canvas.height / layer.imageWidth;
    } else if (layer.imageHeight > layer.canvas.width) {
      scaleAmount = layer.canvas.width / layer.imageHeight;
    }

    // Make sure we never scale up
    scaleAmount = scaleAmount < 1 ? scaleAmount : 1;
  }

  return scaleAmount;
}

// Get the mouse coordinates relative to the canvas and applied transformations
export function getTransformedCoordinates(layer, {
  pageX,
  pageY,
  canvasBox,
  rotation,
  flipped,
  flopped
}) {
  const { imageWidth, imageHeight } = layer;
  const scaleAmount = getScaleAmount(layer, { rotation });
  const originX = (pageX - canvasBox.left) * (layer.canvas.width / canvasBox.width);
  const originY = (pageY - canvasBox.top) * (layer.canvas.height / canvasBox.height);
  let imageX = 0;
  let imageY = 0;
  let canvasX = 0;
  let canvasY = 0;

  if (rotation === 90) {
    imageX = (layer.canvas.width / 2) - (imageHeight * scaleAmount / 2);
    imageY = (layer.canvas.height / 2) - (imageWidth * scaleAmount / 2);

    canvasX = flipped ? layer.canvas.height - imageY - originY : originY - imageY;
    canvasY = flopped ? originX - imageX : layer.canvas.width - imageX - originX;
  } else if (rotation === 180) {
    imageX = (layer.canvas.width / 2) - (imageWidth * scaleAmount / 2);
    imageY = (layer.canvas.height / 2) - (imageHeight * scaleAmount / 2);

    canvasX = flipped ? originX - imageX : layer.canvas.width - imageX - originX;
    canvasY = flopped ? originY + imageY : layer.canvas.height - imageY - originY;
  } else if (rotation === 270) {
    imageX = (layer.canvas.width / 2) - (imageHeight * scaleAmount / 2);
    imageY = (layer.canvas.height / 2) - (imageWidth * scaleAmount / 2);

    canvasX = flipped ? originY - imageY : layer.canvas.height - imageY - originY;
    canvasY = flopped ? layer.canvas.width - imageX - originX : originX - imageX;
  } else {
    imageX = (layer.canvas.width / 2) - (imageWidth * scaleAmount / 2);
    imageY = (layer.canvas.height / 2) - (imageHeight * scaleAmount / 2);

    canvasX = flipped ? layer.canvas.width - imageX - originX : originX - imageX;
    canvasY = flopped ? layer.canvas.height + imageY - originY : originY - imageY;
  }

  return {
    canvasX: canvasX / scaleAmount,
    canvasY: canvasY / scaleAmount
  };
}

// Draw on a transformed canvas
export function drawTransformed(layer, {
  flipped,
  flopped,
  rotation
}, callback) {
  const scaleAmount = getScaleAmount(layer, { rotation });
  let rad;

  // Add a half rotation if the canvas is flipped or flopped and rotated 1/4 turn
  if (
    (flipped || flopped) &&
    !(flipped && flopped) &&
    (rotation === 90 || rotation === 270)
  ) {
    rad = Math.PI + (rotation * Math.PI / 180);
  } else {
    rad = rotation * Math.PI / 180;
  }

  // Store the current state so we can reset it after applying transformations
  layer.ctx.save();

  // Flip/flop the canvas
  const horizontalScale = scaleAmount * (flipped ? -1 : 1);
  const verticalScale = scaleAmount * (flopped ? -1 : 1);

  // Set the origin to the center of the canvas
  layer.ctx.setTransform(horizontalScale, 0, 0, verticalScale, layer.canvas.width / 2, layer.canvas.height / 2);

  // Rotate the canvas
  layer.ctx.rotate(rad);

  // Call the given function so drawings are applied in a transformed context
  callback();

  // Remove the transformations
  layer.ctx.restore();
}

// Draw an image on a layer with rotatation, flipping and flopping
export function drawImage(layer, {
  image,
  flipped,
  flopped,
  rotation
}) {
  const { imageX, imageY, imageWidth, imageHeight } = layer;

  // Draw the image in the center of the canvas and scale it to fit
  drawTransformed(layer, {
    flipped,
    flopped,
    rotation
  }, () => {
    layer.ctx.drawImage(
      image,
      imageX - layer.canvas.width / 2,
      imageY - layer.canvas.height / 2,
      imageWidth,
      imageHeight
    );
  });
}

// Get the coordinates of the selection with positive values
function getSelectionCoords({ imageX, imageY }, cropWindow) {
  const selectionCoords = {
    x: cropWindow.x + imageX,
    y: cropWindow.y + imageY,
    width: cropWindow.width,
    height: cropWindow.height
  };

  // Normalize the box width
  if (selectionCoords.width < 0) {
    selectionCoords.width *= -1;
    selectionCoords.x -= selectionCoords.width;
  }

  // Normalize the box height
  if (selectionCoords.height < 0) {
    selectionCoords.height *= -1;
    selectionCoords.y -= selectionCoords.height;
  }

  return selectionCoords;
}

// Draw the dark rectangles around the selection given the crop coordinates
export function drawSelectionBackdrop(layer, cropWindow) {
  const drawCoords = getSelectionCoords(layer, cropWindow);
  const { imageX, imageY, imageWidth, imageHeight } = layer;
  const originX = - layer.canvas.width / 2;
  const originY = - layer.canvas.height / 2;

  // Draw top section
  layer.ctx.fillRect(
    originX + imageX,
    originY + imageY,
    drawCoords.x - imageX,
    drawCoords.y - imageY
  );
  layer.ctx.fillRect(
    originX + drawCoords.x,
    originY + imageY,
    drawCoords.width,
    drawCoords.y - imageY
  );
  layer.ctx.fillRect(
    originX + drawCoords.x + drawCoords.width,
    originY + imageY,
    (imageX + imageWidth) - (drawCoords.x + drawCoords.width),
    drawCoords.y - imageY
  );

  // Draw middle section
  layer.ctx.fillRect(
    originX + imageX,
    originY + drawCoords.y,
    drawCoords.x - imageX,
    drawCoords.height
  );
  layer.ctx.fillRect(
    originX + drawCoords.x + drawCoords.width,
    originY + drawCoords.y,
    (imageX + imageWidth) - (drawCoords.x + drawCoords.width),
    drawCoords.height
  );

  // Draw bottom section
  layer.ctx.fillRect(
    originX + imageX,
    originY + drawCoords.y + drawCoords.height,
    drawCoords.x - imageX,
    (imageY + imageHeight) - (drawCoords.y + drawCoords.height)
  );
  layer.ctx.fillRect(
    originX + drawCoords.x,
    originY + drawCoords.y + drawCoords.height,
    drawCoords.width,
    (imageY + imageHeight) - (drawCoords.y + drawCoords.height)
  );
  layer.ctx.fillRect(
    originX + drawCoords.x + drawCoords.width,
    originY + drawCoords.y + drawCoords.height,
    (imageX + imageWidth) - (drawCoords.x + drawCoords.width),
    (imageY + imageHeight) - (drawCoords.y + drawCoords.height)
  );
}

// Draw lines indicating focal points in photographs
export function drawRuleOfThirds(layer, cropWindow) {
  const drawCoords = getSelectionCoords(layer, cropWindow);
  const originX = - layer.canvas.width / 2;
  const originY = - layer.canvas.height / 2;

  // Lines should be dashed
  layer.ctx.setLineDash([10, 8]);

  // Draw the left vertical line
  layer.ctx.beginPath();
  layer.ctx.moveTo(originX + drawCoords.x + (drawCoords.width / 3), originY + drawCoords.y);
  layer.ctx.lineTo(originX + drawCoords.x + (drawCoords.width / 3), originY + drawCoords.y + drawCoords.height);
  layer.ctx.stroke();

  // Draw the right vertical line
  layer.ctx.beginPath();
  layer.ctx.moveTo(originX + drawCoords.x + (drawCoords.width / 3 * 2), originY + drawCoords.y);
  layer.ctx.lineTo(originX + drawCoords.x + (drawCoords.width / 3 * 2), originY + drawCoords.y + drawCoords.height);
  layer.ctx.stroke();

  // Draw the top horizontal line
  layer.ctx.beginPath();
  layer.ctx.moveTo(originX + drawCoords.x, originY + drawCoords.y + (drawCoords.height / 3));
  layer.ctx.lineTo(originX + drawCoords.x + drawCoords.width, originY + drawCoords.y + (drawCoords.height / 3));
  layer.ctx.stroke();

  // Draw the bottom horizontal line
  layer.ctx.beginPath();
  layer.ctx.moveTo(originX + drawCoords.x, originY + drawCoords.y + (drawCoords.height / 3 * 2));
  layer.ctx.lineTo(originX + drawCoords.x + drawCoords.width, originY + drawCoords.y + (drawCoords.height / 3 * 2));
  layer.ctx.stroke();
}

// Draw the resize handles on the selection window
export function drawResizeHandles(layer, cropWindow, { handleSize, ratio }) {
  const drawCoords = getSelectionCoords(layer, cropWindow);
  const originX = - layer.canvas.width / 2;
  const originY = - layer.canvas.height / 2;

  layer.ctx.fillRect(
    originX + drawCoords.x - (handleSize / 2),
    originY + drawCoords.y - (handleSize / 2),
    handleSize,
    handleSize
  );
  layer.ctx.fillRect(
    originX + drawCoords.x + drawCoords.width - (handleSize / 2),
    originY + drawCoords.y - (handleSize / 2),
    handleSize,
    handleSize
  );
  layer.ctx.fillRect(
    originX + drawCoords.x - (handleSize / 2),
    originY + drawCoords.y + drawCoords.height - (handleSize / 2),
    handleSize,
    handleSize
  );
  layer.ctx.fillRect(
    originX + drawCoords.x + drawCoords.width - (handleSize / 2),
    originY + drawCoords.y + drawCoords.height - (handleSize / 2),
    handleSize,
    handleSize
  );

  // Draw side resize handles if we're not fixing a ratio
  if (!ratio) {
    layer.ctx.fillRect(
      originX + drawCoords.x + (drawCoords.width / 2) - (handleSize / 2),
      originY + drawCoords.y - (handleSize / 2),
      handleSize,
      handleSize
    );
    layer.ctx.fillRect(
      originX + drawCoords.x + drawCoords.width - (handleSize / 2),
      originY + drawCoords.y + (drawCoords.height / 2) - (handleSize / 2),
      handleSize,
      handleSize
    );
    layer.ctx.fillRect(
      originX + drawCoords.x + (drawCoords.width / 2) - (handleSize / 2),
      originY + drawCoords.y + drawCoords.height - (handleSize / 2),
      handleSize,
      handleSize
    );
    layer.ctx.fillRect(
      originX + drawCoords.x - (handleSize / 2),
      originY + drawCoords.y + (drawCoords.height / 2) - (handleSize / 2),
      handleSize,
      handleSize
    );
  }
}



// WEBPACK FOOTER //
// ./src/modules/ImageCrop/util.js